import type { User } from 'firebase/auth'

// User role types
export type UserRole = 'user' | 'admin'

// Custom claims interface
export interface CustomClaims {
  role?: UserRole
  // Add other custom claims as needed
}

export interface AuthUser {
  uid: string
  email: string | null
  displayName: string | null
  photoURL: string | null
  emailVerified: boolean
  // Add custom claims and role information
  customClaims: CustomClaims
  role: UserRole
  isAdmin: boolean // Computed from role
}

export interface AuthState {
  user: AuthUser | null
  loading: boolean
  error: string | null
  claimsLoading: boolean // Separate loading state for custom claims
}

export interface AuthContextType extends AuthState {
  signInWithGoogle: () => Promise<void>
  signInWithApple: () => Promise<void>
  signInWithEmail: (email: string, password: string) => Promise<void>
  signUpWithEmail: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  clearError: () => void
  refreshClaims: () => Promise<void> // Method to refresh custom claims
  isEmulatorMode: boolean
  // Role checking helper methods
  isUserRole: (role: UserRole) => boolean
  requireRole: (role: UserRole) => boolean
}

export type AuthProvider = 'google' | 'apple'

export interface SignInModalProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
}

// Convert Firebase User to AuthUser
export const mapFirebaseUser = (
  user: User,
  customClaims: CustomClaims = {}
): AuthUser => {
  const role: UserRole = customClaims.role || 'user'
  const isAdmin = role === 'admin'

  return {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
    photoURL: user.photoURL,
    emailVerified: user.emailVerified,
    customClaims,
    role,
    isAdmin,
  }
}

// Helper function to determine user role from custom claims
export const getUserRole = (customClaims: CustomClaims): UserRole => {
  return customClaims.role || 'user'
}

// Helper function to check if user has specific role
export const hasRole = (user: AuthUser | null, role: UserRole): boolean => {
  if (!user) return false
  return user.role === role || (role === 'user' && user.role === 'admin') // Admin inherits user permissions
}

// Helper function to check if user is admin
export const isAdmin = (user: AuthUser | null): boolean => {
  return user?.role === 'admin'
}
